<template>
  <Teleport to="body">
    <div
      v-if="props.modelValue"
      class="ann-dialog-mask"
      role="presentation"
      @click.self="handleClose"
      @touchmove.prevent
      @wheel.prevent
    >
      <div
        ref="contentRef"
        class="ann-dialog-content"
        role="dialog"
        aria-modal="true"
        :style="{ '--ann-dialog-scale': dialogScale }"
        @touchmove.stop
        @wheel.stop
      >
        <slot />
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { defineEmits, defineProps, nextTick, onUnmounted, ref, watch } from 'vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:modelValue'])
const contentRef = ref(null)
const dialogScale = ref(1)
const DIALOG_VIEWPORT_GAP = 32
const DESKTOP_MIN_WIDTH = 900
const DESKTOP_MAX_HEIGHT_RATIO = 0.82
const DESKTOP_MIN_TARGET_HEIGHT = 720

const lockScroll = () => {
  document.body.classList.add('no-scroll')
  document.documentElement.classList.add('no-scroll')
}

const unlockScroll = () => {
  document.body.classList.remove('no-scroll')
  document.documentElement.classList.remove('no-scroll')
}

const handleClose = () => {
  emit('update:modelValue', false)
}

const updateDialogScale = async () => {
  await nextTick()
  const content = contentRef.value
  if (!props.modelValue || !content) {
    dialogScale.value = 1
    return
  }

  const width = content.offsetWidth || content.scrollWidth
  const height = content.offsetHeight || content.scrollHeight
  if (!width || !height) {
    dialogScale.value = 1
    return
  }

  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight
  const baseAvailableHeight = Math.max(
    viewportHeight - DIALOG_VIEWPORT_GAP,
    1,
  )
  const desktopTargetHeight = Math.max(
    viewportHeight * DESKTOP_MAX_HEIGHT_RATIO,
    DESKTOP_MIN_TARGET_HEIGHT,
  )
  const availableWidth = Math.max(viewportWidth - DIALOG_VIEWPORT_GAP, 1)
  const availableHeight =
    viewportWidth >= DESKTOP_MIN_WIDTH
      ? Math.min(baseAvailableHeight, desktopTargetHeight)
      : baseAvailableHeight
  const nextScale = Math.min(
    1,
    availableWidth / width,
    availableHeight / height,
  )

  dialogScale.value =
    Number.isFinite(nextScale) && nextScale > 0 ? nextScale : 1
}

watch(
  () => props.modelValue,
  (visible) => {
    if (visible) {
      lockScroll()
      updateDialogScale()
    } else {
      unlockScroll()
      dialogScale.value = 1
    }
  },
  { immediate: true },
)

window.addEventListener('resize', updateDialogScale)

onUnmounted(() => {
  window.removeEventListener('resize', updateDialogScale)
  unlockScroll()
})
</script>

<style lang="less" scoped>
.ann-dialog-mask {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.65);
}

.ann-dialog-content {
  --ann-close-top: -50px;
  --ann-close-right: 10px;

  position: relative;
  flex: none;
  overflow: visible;
  transform: scale(var(--ann-dialog-scale, 1));
  transform-origin: center;
}

:global(html.no-scroll),
:global(body.no-scroll) {
  overflow: hidden;
}
</style>
